import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { InvoiceParsedRow } from "types/api/invoiceParseJobs";
import { PHOTO_ACCEPT, PHOTO_EXTENSIONS } from "utils/photoUpload";
import { toastResponseError } from "utils/responseMessageHelper";
import { useInvoiceParse } from "../hooks/useInvoiceParse";
import { ApplyButton, Hint, Zone } from "./DeclarationFileDropZone.styles";

const SPREADSHEET_EXTENSIONS = ["csv", "xlsx", "xls"];
const SPREADSHEET_ACCEPT =
  ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";
const RECEIPT_EXTENSIONS = [...PHOTO_EXTENSIONS, "pdf"];
const RECEIPT_ACCEPT = `${PHOTO_ACCEPT},application/pdf`;

interface DeclarationFileDropZoneProps {
  // The declaration import; settles when the import (not the refetch) is over.
  onImportFile: (file: File) => Promise<unknown>;
  onRows: (rows: InvoiceParsedRow[]) => void;
  receiptsEnabled: boolean;
}

// One zone for both ways of filling the declaration from a file; the extension
// picks the way (dropped CSVs often carry no usable MIME type). A spreadsheet
// is imported on the server and replaces the declaration. A receipt photo or
// PDF becomes rows appended to the form for review; its failures are shown
// inline and the customer can always keep typing rows by hand.
const DeclarationFileDropZone: FC<DeclarationFileDropZoneProps> = ({
  onImportFile,
  onRows,
  receiptsEnabled,
}) => {
  const { t } = useTranslation("common");
  const { phase, start, keepWaiting, retry } = useInvoiceParse({ onRows });
  const [isImporting, setIsImporting] = useState(false);

  const handleFile = useCallback(
    (file: File) => {
      const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (SPREADSHEET_EXTENSIONS.includes(extension)) {
        setIsImporting(true);
        onImportFile(file).finally(() => setIsImporting(false));
      } else if (receiptsEnabled && RECEIPT_EXTENSIONS.includes(extension)) {
        start(file);
      } else {
        toastResponseError(t("declarationFile.unsupported"));
      }
    },
    [onImportFile, receiptsEnabled, start, t],
  );

  return (
    <>
      <Zone
        onFile={handleFile}
        accept={
          receiptsEnabled
            ? `${SPREADSHEET_ACCEPT}, ${RECEIPT_ACCEPT}`
            : SPREADSHEET_ACCEPT
        }
        text={t("declarationFile.drop")}
        buttonText={t("declarationFile.choose")}
        hint={
          <>
            {t("declarationFile.formatsSpreadsheet")}
            {receiptsEnabled && (
              <>
                <br />
                {t("declarationFile.formatsReceipt")}
              </>
            )}
          </>
        }
        isLoading={isImporting || phase.name === "parsing"}
      />
      {phase.name === "failed" && (
        <Hint $warning role="alert" data-testid="invoice-parse-status">
          {phase.failure.kind === "unavailable"
            ? t("invoiceParse.unavailable")
            : t("invoiceParse.failed")}
          {phase.failure.kind === "generic" && phase.failure.detail
            ? ` (${phase.failure.detail})`
            : ""}
          {phase.failure.kind !== "unavailable" && (
            <ApplyButton type="button" onClick={retry}>
              {t("invoiceParse.tryAgain")}
            </ApplyButton>
          )}
        </Hint>
      )}
      {(phase.name === "parsing" || phase.name === "timeout") && (
        <Hint $warning={false} role="status" aria-live="polite" data-testid="invoice-parse-status">
          {phase.name === "timeout" ? t("invoiceParse.timeout") : t("invoiceParse.parsing")}
          {phase.name === "timeout" && (
            <ApplyButton type="button" onClick={keepWaiting}>
              {t("invoiceParse.keepWaiting")}
            </ApplyButton>
          )}
        </Hint>
      )}
    </>
  );
};

export default DeclarationFileDropZone;
